import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, Badge, ActionButton } from '@/components/ui'
import { dayAfter, lastDayBefore } from '@/lib/assignmentBoundary'
import { PeriodSelector } from '@/components/analytics'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  listTerritorialManagers,
  getManagerPeriodTotals,
  getManagerPartnerPeriodDetails,
  getManagerPartnerWeeklyRevenue,
  getStudioDailyCallsRows,
  getStudioWeeklyPlanFactRows,
  unassignTerritorialManager,
  setTerritorialManagerLastDay,
  listFormerTerritorialManagers,
  cancelScheduledTuRemoval,
  cancelScheduledAssignmentClose,
  listManagerClosedAssignments,
  type StudioWeeklyPlanFactRow,
  type TuRemovalResult,
} from '@/services/territorialManagerService'
import type {
  TerritorialManagerListItem,
  TerritorialManagerPeriodTotals,
  TerritorialManagerPartnerPeriodDetailRow,
  TerritorialManagerWeeklyRevenueRow,
  TerritorialManagerClosedAssignment,
} from '@/types'
import { formatCurrency, formatNumberRounded, formatPercentage, formatPartnerDisplay } from '@/lib/utils'
import {
  ArrowLeft,
  Building2,
  Users,
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  X,
  ArrowUpRight,
  Search,
  CircleDashed,
} from 'lucide-react'

type Status = 'ok' | 'warn' | 'bad' | 'no-plan'
type Filter = 'all' | 'problems' | 'no-plan' | 'on-track'
type SortKey = 'deviation' | 'revenue' | 'name'

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function diffDaysInclusive(fromISO: string, toISO: string): number {
  const a = new Date(fromISO + 'T00:00:00Z').getTime()
  const b = new Date(toISO + 'T00:00:00Z').getTime()
  return Math.max(0, Math.round((b - a) / 86400000) + 1)
}

function periodProgress(periodStart: string, periodEnd: string): number {
  const t = todayISO()
  if (t < periodStart) return 0
  if (t > periodEnd) return 1
  const total = diffDaysInclusive(periodStart, periodEnd)
  const passed = diffDaysInclusive(periodStart, t)
  if (total <= 0) return 0
  return Math.min(1, Math.max(0, passed / total))
}

function metricStatus(plan: number | null | undefined, fact: number | null | undefined, expected: number): Status {
  const p = plan ?? 0
  const f = fact ?? 0
  if (!p) return 'no-plan'
  const factPct = f / p
  const baseline = expected > 0 ? expected : 0.01
  const ratio = factPct / baseline
  if (ratio >= 0.95) return 'ok'
  if (ratio >= 0.75) return 'warn'
  return 'bad'
}

function rateStatus(plan: number | null | undefined, fact: number | null | undefined): Status {
  const p = plan ?? 0
  const f = fact ?? 0
  if (!p) return 'no-plan'
  const ratio = f / p
  if (ratio >= 0.95) return 'ok'
  if (ratio >= 0.75) return 'warn'
  return 'bad'
}

function worseStatus(a: Status, b: Status): Status {
  const order: Record<Status, number> = { ok: 0, warn: 1, 'no-plan': 2, bad: 3 }
  return order[a] >= order[b] ? a : b
}

function statusStripe(status: Status): string {
  switch (status) {
    case 'ok': return 'bg-success'
    case 'warn': return 'bg-primary'
    case 'bad': return 'bg-error'
    case 'no-plan': return 'bg-muted-foreground/40'
  }
}

function statusTextColor(status: Status): string {
  switch (status) {
    case 'ok': return 'text-success'
    case 'warn': return 'text-primary'
    case 'bad': return 'text-error'
    case 'no-plan': return 'text-muted-foreground'
  }
}

function statusDot(status: Status): string {
  switch (status) {
    case 'ok': return 'bg-success'
    case 'warn': return 'bg-primary'
    case 'bad': return 'bg-error'
    case 'no-plan': return 'bg-muted-foreground/40'
  }
}

function formatPct(value: number): string {
  return `${Math.round(value)}%`
}

function fmtDayMonth(iso: string): string {
  // "2026-05-14" → "14.05"
  const [, mm, dd] = iso.split('-')
  return `${dd}.${mm}`
}

function fmtDate(iso: string): string {
  // "2026-05-14" → "14.05.2026"
  const [yyyy, mm, dd] = iso.split('-')
  return `${dd}.${mm}.${yyyy}`
}

function formatPlainValue(value: number): string {
  return formatNumberRounded(value)
}

type WeeklyAggregate = {
  week_id: number
  n: number
  start: string
  end: string
  plan: number | null
  fact: number | null
  status: Status
}

function aggregateWeeks(rows: TerritorialManagerWeeklyRevenueRow[]): WeeklyAggregate[] {
  const map = new Map<number, { n: number; start: string; end: string; planSum: number; planAny: boolean; factSum: number; factAny: boolean }>()
  for (const r of rows) {
    const bucket = map.get(r.week_id) ?? {
      n: r.week_number_in_period,
      start: r.week_start,
      end: r.week_end,
      planSum: 0,
      planAny: false,
      factSum: 0,
      factAny: false,
    }
    if (r.gross_revenue_plan_rub != null) {
      bucket.planSum += r.gross_revenue_plan_rub
      bucket.planAny = true
    }
    if (r.gross_revenue_fact_rub != null) {
      bucket.factSum += r.gross_revenue_fact_rub
      bucket.factAny = true
    }
    map.set(r.week_id, bucket)
  }
  return Array.from(map.entries())
    .map(([week_id, b]) => {
      const plan = b.planAny ? b.planSum : null
      const fact = b.factAny ? b.factSum : null
      return { week_id, n: b.n, start: b.start, end: b.end, plan, fact, status: rateStatus(plan, fact) }
    })
    .sort((a, b) => a.n - b.n)
}

// Compact number for in-row cells: 1 234 567 → "1.2M", 12 345 → "12k", 800 → "800".
function formatCompact(value: number | null | undefined): string {
  if (value == null) return '—'
  const abs = Math.abs(value)
  if (abs >= 1_000_000) {
    const v = value / 1_000_000
    return (abs >= 10_000_000 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, '')) + 'M'
  }
  if (abs >= 10_000) return Math.round(value / 1_000) + 'k'
  if (abs >= 1_000) return Math.round(value).toLocaleString('ru-RU')
  return String(Math.round(value))
}

type AvgCheckStats = {
  planNewCount: number
  factNewCount: number
  planNewRevenue: number
  factNewRevenue: number
  planExistingCount: number
  factExistingCount: number
  planExistingRevenue: number
  factExistingRevenue: number
}

function buildAvgCheckStatsByStudio(rows: StudioWeeklyPlanFactRow[]): Map<number, AvgCheckStats> {
  const map = new Map<number, AvgCheckStats>()
  for (const row of rows) {
    const stats = map.get(row.studio_internal_id) ?? {
      planNewCount: 0,
      factNewCount: 0,
      planNewRevenue: 0,
      factNewRevenue: 0,
      planExistingCount: 0,
      factExistingCount: 0,
      planExistingRevenue: 0,
      factExistingRevenue: 0,
    }

    stats.planNewCount += (row.weekly_new_sales_plan || 0) + (row.weekly_new_subscriptions_count_plan || 0)
    stats.factNewCount += (row.weekly_new_sales_fact || 0) + (row.weekly_new_subscriptions_count_fact || 0)
    stats.planNewRevenue += (row.weekly_new_sales_amount_plan || 0) + (row.weekly_new_subscriptions_amount_plan || 0)
    stats.factNewRevenue += (row.weekly_new_sales_amount_fact || 0) + (row.weekly_new_subscriptions_amount_fact || 0)

    stats.planExistingCount +=
      (row.weekly_renewals_count_plan || 0) +
      (row.weekly_upgrades_count_plan || 0) +
      (row.weekly_returning_clients_count_plan || 0) +
      (row.weekly_subscription_renewals_count_plan || 0) +
      (row.weekly_subscription_upgrades_count_plan || 0) +
      (row.weekly_subscription_returning_clients_count_plan || 0)
    stats.factExistingCount +=
      (row.weekly_renewals_count_fact || 0) +
      (row.weekly_upgrades_count_fact || 0) +
      (row.weekly_returning_clients_count_fact || 0) +
      (row.weekly_subscription_renewals_count_fact || 0) +
      (row.weekly_subscription_upgrades_count_fact || 0) +
      (row.weekly_subscription_returning_clients_count_fact || 0)
    stats.planExistingRevenue +=
      (row.weekly_renewals_amount_plan || 0) +
      (row.weekly_upgrades_amount_plan || 0) +
      (row.weekly_returning_clients_amount_plan || 0) +
      (row.weekly_subscription_renewals_amount_plan || 0) +
      (row.weekly_subscription_upgrades_amount_plan || 0) +
      (row.weekly_subscription_returning_clients_amount_plan || 0)
    stats.factExistingRevenue +=
      (row.weekly_renewals_amount_fact || 0) +
      (row.weekly_upgrades_amount_fact || 0) +
      (row.weekly_returning_clients_amount_fact || 0) +
      (row.weekly_subscription_renewals_amount_fact || 0) +
      (row.weekly_subscription_upgrades_amount_fact || 0) +
      (row.weekly_subscription_returning_clients_amount_fact || 0)

    map.set(row.studio_internal_id, stats)
  }
  return map
}

export function TerritorialManagerDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const periods = useStudioPeriods()
  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange,
  } = useAnalyticsTimeFilter(periods.currentPeriod)

  const [managerInfo, setManagerInfo] = useState<TerritorialManagerListItem | null>(null)
  const [periodTotals, setPeriodTotals] = useState<TerritorialManagerPeriodTotals[]>([])
  const [partnerDetailRows, setPartnerDetailRows] = useState<TerritorialManagerPartnerPeriodDetailRow[]>([])
  const [weekDetailRows, setWeekDetailRows] = useState<TerritorialManagerPartnerPeriodDetailRow[]>([])
  const [periodAvgCheckRows, setPeriodAvgCheckRows] = useState<TerritorialManagerPartnerPeriodDetailRow[]>([])
  const [weeklyRevenueRows, setWeeklyRevenueRows] = useState<TerritorialManagerWeeklyRevenueRow[]>([])
  const [loading, setLoading] = useState(true)
  const [weekLoading, setWeekLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAdvanced, setIsAdvanced] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const [removeTitleOpen, setRemoveTitleOpen] = useState(false)
  const [removeTitleDate, setRemoveTitleDate] = useState(todayISO)
  const [removePreview, setRemovePreview] = useState<TuRemovalResult | null>(null)
  const [removePreviewError, setRemovePreviewError] = useState<string | null>(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [unassignFormId, setUnassignFormId] = useState<number | null>(null)
  const [unassignDate, setUnassignDate] = useState(todayISO)
  const [expandedPartnerIds, setExpandedPartnerIds] = useState<number[]>([])

  // Снятые франчайзи: витрина активных назначений их не показывает, поэтому без
  // отдельного блока ошибочную дату снятия исправить неоткуда.
  const [closedAssignments, setClosedAssignments] = useState<TerritorialManagerClosedAssignment[]>([])
  const [closedOpen, setClosedOpen] = useState(false)
  const [closedFormId, setClosedFormId] = useState<number | null>(null)
  const [closedDate, setClosedDate] = useState(todayISO)

  const [filter, setFilter] = useState<Filter>('all')
  const [sortKey, setSortKey] = useState<SortKey>('deviation')
  const [search, setSearch] = useState('')

  const reload = () => {
    const selectedPeriodId = periods.currentPeriod?.period_id ?? null
    setLoading(true)
    Promise.all([
      listTerritorialManagers(),
      // Снятые с должности в общий список не входят — без этого их карточка не открывается
      // и дату снятия поправить негде. Ошибка не роняет экран.
      listFormerTerritorialManagers().catch(() => [] as TerritorialManagerListItem[]),
      getManagerPeriodTotals(userId),
      getManagerPartnerPeriodDetails(userId, selectedPeriodId),
      getManagerPartnerWeeklyRevenue(userId, selectedPeriodId),
    ])
      .then(([list, former, totals, detailRows, weeklyRows]) => {
        setManagerInfo(
          list.find(i => i.manager_user_id === userId)
            ?? former.find(i => i.manager_user_id === userId)
            ?? null
        )
        setPeriodTotals(totals)
        setPartnerDetailRows(detailRows)
        setWeeklyRevenueRows(weeklyRows)
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))

    // Отдельным запросом и с глушением ошибки: RPC появляется вместе с миграцией
    // 20260807120000, до её применения блок просто не рисуется, а остальной экран
    // грузится как раньше.
    listManagerClosedAssignments(userId)
      .then(setClosedAssignments)
      .catch(() => setClosedAssignments([]))
  }

  useEffect(() => {
    if (!userId || periods.loading || !periods.currentPeriod) return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, periods.loading, periods.currentPeriod?.period_id])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) return
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  useEffect(() => {
    let cancelled = false

    const buildWeekDetailRows = async () => {
      if (
        !periods.currentPeriod?.period_id ||
        partnerDetailRows.length === 0
      ) {
        if (!cancelled) {
          setWeekDetailRows([])
          setPeriodAvgCheckRows([])
        }
        return
      }

      try {
        setWeekLoading(true)
        const studioRows = partnerDetailRows.filter((row) => row.studio_internal_id != null)
        const studioIds = Array.from(new Set(studioRows.map((row) => row.studio_internal_id!).filter(Boolean)))

        if (selectedRange?.mode !== 'week') {
          const weeklyStudioRows = await getStudioWeeklyPlanFactRows({
            studioIds,
            periodId: periods.currentPeriod.period_id,
          })
          const statsByStudioId = buildAvgCheckStatsByStudio(weeklyStudioRows)
          const rowsByPartner = new Map<number, TerritorialManagerPartnerPeriodDetailRow[]>()

          partnerDetailRows
            .filter((row) => row.studio_internal_id != null)
            .forEach((row) => {
              const list = rowsByPartner.get(row.partner_number) ?? []
              list.push(row)
              rowsByPartner.set(row.partner_number, list)
            })

          const nextRows = Array.from(rowsByPartner.values()).flatMap((rows) => {
            let partnerPlanNewCount = 0
            let partnerFactNewCount = 0
            let partnerPlanNewRevenue = 0
            let partnerFactNewRevenue = 0
            let partnerPlanExistingCount = 0
            let partnerFactExistingCount = 0
            let partnerPlanExistingRevenue = 0
            let partnerFactExistingRevenue = 0

            rows.forEach((row) => {
              const stats = statsByStudioId.get(row.studio_internal_id!)
              if (!stats) return
              partnerPlanNewCount += stats.planNewCount
              partnerFactNewCount += stats.factNewCount
              partnerPlanNewRevenue += stats.planNewRevenue
              partnerFactNewRevenue += stats.factNewRevenue
              partnerPlanExistingCount += stats.planExistingCount
              partnerFactExistingCount += stats.factExistingCount
              partnerPlanExistingRevenue += stats.planExistingRevenue
              partnerFactExistingRevenue += stats.factExistingRevenue
            })

            const partnerPlanAvgNew = partnerPlanNewCount > 0 ? partnerPlanNewRevenue / partnerPlanNewCount : null
            const partnerFactAvgNew = partnerFactNewCount > 0 ? partnerFactNewRevenue / partnerFactNewCount : null
            const partnerPlanAvgExisting = partnerPlanExistingCount > 0 ? partnerPlanExistingRevenue / partnerPlanExistingCount : null
            const partnerFactAvgExisting = partnerFactExistingCount > 0 ? partnerFactExistingRevenue / partnerFactExistingCount : null

            return rows.map((row) => {
              const stats = statsByStudioId.get(row.studio_internal_id!)
              return {
                ...row,
                partner_plan_avg_check_new: partnerPlanAvgNew,
                partner_fact_avg_check_new: partnerFactAvgNew,
                partner_plan_avg_check_existing: partnerPlanAvgExisting,
                partner_fact_avg_check_existing: partnerFactAvgExisting,
                studio_plan_avg_check_new: stats && stats.planNewCount > 0 ? stats.planNewRevenue / stats.planNewCount : null,
                studio_fact_avg_check_new: stats && stats.factNewCount > 0 ? stats.factNewRevenue / stats.factNewCount : null,
                studio_plan_avg_check_existing: stats && stats.planExistingCount > 0 ? stats.planExistingRevenue / stats.planExistingCount : null,
                studio_fact_avg_check_existing: stats && stats.factExistingCount > 0 ? stats.factExistingRevenue / stats.factExistingCount : null,
              }
            })
          })

          if (!cancelled) {
            setPeriodAvgCheckRows(nextRows)
            setWeekDetailRows([])
          }
          return
        }

        if (!selectedRange.weekNumber) {
          if (!cancelled) setWeekDetailRows([])
          return
        }

        const [weeklyStudioRows, dailyCallsRows] = await Promise.all([
          getStudioWeeklyPlanFactRows({
            studioIds,
            periodId: periods.currentPeriod.period_id,
            weekNumber: selectedRange.weekNumber,
          }),
          getStudioDailyCallsRows({
            studioIds,
            dateFrom: selectedRange.start,
            dateTo: selectedRange.end,
          }),
        ])

        const weekByStudioId = new Map(
          weeklyStudioRows.map((row) => [row.studio_internal_id, row])
        )
        const callsByStudioId = new Map<number, { calls: number; answered: number }>()
        dailyCallsRows.forEach((row) => {
          const current = callsByStudioId.get(row.studio_internal_id) ?? { calls: 0, answered: 0 }
          current.calls += row.calls_count || 0
          current.answered += row.answered_calls_count || 0
          callsByStudioId.set(row.studio_internal_id, current)
        })
        const rowsByPartner = new Map<number, TerritorialManagerPartnerPeriodDetailRow[]>()

        partnerDetailRows
          .filter((row) => row.studio_internal_id != null)
          .forEach((row) => {
            const list = rowsByPartner.get(row.partner_number) ?? []
            list.push(row)
            rowsByPartner.set(row.partner_number, list)
          })

        const nextRows = Array.from(rowsByPartner.values()).flatMap((rows) => {
          const studioWeekRows = rows.map((source) => {
            const week = weekByStudioId.get(source.studio_internal_id!)
            const calls = callsByStudioId.get(source.studio_internal_id!)
            const planSalesCount = (week?.weekly_new_sales_plan || 0) + (week?.weekly_new_subscriptions_count_plan || 0)
            const factSalesCount = (week?.weekly_new_sales_fact || 0) + (week?.weekly_new_subscriptions_count_fact || 0)
            const planExistingCount =
              (week?.weekly_renewals_count_plan || 0) +
              (week?.weekly_upgrades_count_plan || 0) +
              (week?.weekly_returning_clients_count_plan || 0) +
              (week?.weekly_subscription_renewals_count_plan || 0) +
              (week?.weekly_subscription_upgrades_count_plan || 0) +
              (week?.weekly_subscription_returning_clients_count_plan || 0)
            const factExistingCount =
              (week?.weekly_renewals_count_fact || 0) +
              (week?.weekly_upgrades_count_fact || 0) +
              (week?.weekly_returning_clients_count_fact || 0) +
              (week?.weekly_subscription_renewals_count_fact || 0) +
              (week?.weekly_subscription_upgrades_count_fact || 0) +
              (week?.weekly_subscription_returning_clients_count_fact || 0)

            return {
              source,
              planGrossRevenue: week?.weekly_gross_revenue_plan || 0,
              factGrossRevenue: week?.weekly_gross_revenue_fact || 0,
              planMarketing: week?.weekly_marketing_budget_plan || 0,
              factMarketing: week?.weekly_marketing_expenses_amount_fact || 0,
              planLeads: week?.weekly_leads_plan || 0,
              factLeads: week?.weekly_leads_fact || 0,
              factCalls: calls?.calls || 0,
              factAnsweredCalls: calls?.answered || 0,
              planAppointments: week?.weekly_appointments_plan || 0,
              factAppointments: week?.weekly_appointments_fact || 0,
              planVisitors: week?.weekly_new_visitors_plan || 0,
              factVisitors: week?.weekly_new_visitors_fact || 0,
              planSalesCount,
              factSalesCount,
              planExistingCount,
              factExistingCount,
              planRefunds: week?.weekly_refunds_amount_plan ?? null,
              factRefunds: week?.weekly_refunds_amount_fact || 0,
              planAvgCheckNew: planSalesCount > 0
                ? (
                    (week?.weekly_new_sales_amount_plan || 0) +
                    (week?.weekly_new_subscriptions_amount_plan || 0)
                  ) / planSalesCount
                : null,
              factAvgCheckNew: factSalesCount > 0
                ? (
                    (week?.weekly_new_sales_amount_fact || 0) +
                    (week?.weekly_new_subscriptions_amount_fact || 0)
                  ) / factSalesCount
                : null,
              planAvgCheckExisting: planExistingCount > 0
                ? (
                    (week?.weekly_renewals_amount_plan || 0) +
                    (week?.weekly_upgrades_amount_plan || 0) +
                    (week?.weekly_returning_clients_amount_plan || 0) +
                    (week?.weekly_subscription_renewals_amount_plan || 0) +
                    (week?.weekly_subscription_upgrades_amount_plan || 0) +
                    (week?.weekly_subscription_returning_clients_amount_plan || 0)
                  ) / planExistingCount
                : null,
              factAvgCheckExisting: factExistingCount > 0
                ? (
                    (week?.weekly_renewals_amount_fact || 0) +
                    (week?.weekly_upgrades_amount_fact || 0) +
                    (week?.weekly_returning_clients_amount_fact || 0) +
                    (week?.weekly_subscription_renewals_amount_fact || 0) +
                    (week?.weekly_subscription_upgrades_amount_fact || 0) +
                    (week?.weekly_subscription_returning_clients_amount_fact || 0)
                  ) / factExistingCount
                : null,
            }
          })

          const partnerPlanGrossRevenue = studioWeekRows.reduce((sum, row) => sum + row.planGrossRevenue, 0)
          const partnerFactGrossRevenue = studioWeekRows.reduce((sum, row) => sum + row.factGrossRevenue, 0)
          const partnerPlanMarketing = studioWeekRows.reduce((sum, row) => sum + row.planMarketing, 0)
          const partnerFactMarketing = studioWeekRows.reduce((sum, row) => sum + row.factMarketing, 0)
          const partnerPlanLeads = studioWeekRows.reduce((sum, row) => sum + row.planLeads, 0)
          const partnerFactLeads = studioWeekRows.reduce((sum, row) => sum + row.factLeads, 0)
          const partnerFactCalls = studioWeekRows.reduce((sum, row) => sum + row.factCalls, 0)
          const partnerFactAnsweredCalls = studioWeekRows.reduce((sum, row) => sum + row.factAnsweredCalls, 0)
          const partnerPlanAppointments = studioWeekRows.reduce((sum, row) => sum + row.planAppointments, 0)
          const partnerFactAppointments = studioWeekRows.reduce((sum, row) => sum + row.factAppointments, 0)
          const partnerPlanVisitors = studioWeekRows.reduce((sum, row) => sum + row.planVisitors, 0)
          const partnerFactVisitors = studioWeekRows.reduce((sum, row) => sum + row.factVisitors, 0)
          const partnerPlanSales = studioWeekRows.reduce((sum, row) => sum + row.planSalesCount, 0)
          const partnerFactSales = studioWeekRows.reduce((sum, row) => sum + row.factSalesCount, 0)
          const partnerPlanExisting = studioWeekRows.reduce((sum, row) => sum + row.planExistingCount, 0)
          const partnerFactExisting = studioWeekRows.reduce((sum, row) => sum + row.factExistingCount, 0)
          const partnerPlanRefunds = studioWeekRows.reduce((sum, row) => sum + (row.planRefunds || 0), 0)
          const partnerFactRefunds = studioWeekRows.reduce((sum, row) => sum + row.factRefunds, 0)
          const avgNewPlanNum = studioWeekRows.reduce((sum, row) => sum + ((row.planAvgCheckNew || 0) * row.planSalesCount), 0)
          const avgNewFactNum = studioWeekRows.reduce((sum, row) => sum + ((row.factAvgCheckNew || 0) * row.factSalesCount), 0)
          const avgExPlanNum = studioWeekRows.reduce((sum, row) => sum + ((row.planAvgCheckExisting || 0) * row.planExistingCount), 0)
          const avgExFactNum = studioWeekRows.reduce((sum, row) => sum + ((row.factAvgCheckExisting || 0) * row.factExistingCount), 0)

          return studioWeekRows.map((row) => ({
            ...row.source,
            period_start: selectedRange.start,
            period_end: selectedRange.end,
            partner_plan_gross_revenue: partnerPlanGrossRevenue,
            partner_fact_gross_revenue: partnerFactGrossRevenue,
            partner_plan_marketing_budget: partnerPlanMarketing,
            partner_fact_marketing_spent: partnerFactMarketing,
            partner_plan_leads_count: partnerPlanLeads,
            partner_fact_leads_count: partnerFactLeads,
            partner_plan_calls_count: null,
            partner_fact_calls_count: partnerFactCalls,
            partner_plan_answered_calls_count: null,
            partner_fact_answered_calls_count: partnerFactAnsweredCalls,
            partner_plan_appointments_count: partnerPlanAppointments,
            partner_fact_appointments_count: partnerFactAppointments,
            partner_plan_new_visitors_count: partnerPlanVisitors,
            partner_fact_new_visitors_count: partnerFactVisitors,
            partner_plan_sales_count: partnerPlanSales,
            partner_fact_sales_count: partnerFactSales,
            partner_plan_booking_conversion: partnerPlanLeads > 0 ? (partnerPlanAppointments / partnerPlanLeads) * 100 : null,
            partner_fact_booking_conversion: partnerFactLeads > 0 ? (partnerFactAppointments / partnerFactLeads) * 100 : null,
            partner_plan_visit_conversion: partnerPlanAppointments > 0 ? (partnerPlanVisitors / partnerPlanAppointments) * 100 : null,
            partner_fact_visit_conversion: partnerFactAppointments > 0 ? (partnerFactVisitors / partnerFactAppointments) * 100 : null,
            partner_plan_sales_conversion: partnerPlanVisitors > 0 ? (partnerPlanSales / partnerPlanVisitors) * 100 : null,
            partner_fact_sales_conversion: partnerFactVisitors > 0 ? (partnerFactSales / partnerFactVisitors) * 100 : null,
            partner_plan_avg_check_new: partnerPlanSales > 0 ? avgNewPlanNum / partnerPlanSales : null,
            partner_fact_avg_check_new: partnerFactSales > 0 ? avgNewFactNum / partnerFactSales : null,
            partner_plan_avg_check_existing: partnerPlanExisting > 0 ? avgExPlanNum / partnerPlanExisting : null,
            partner_fact_avg_check_existing: partnerFactExisting > 0 ? avgExFactNum / partnerFactExisting : null,
            partner_plan_refunds_amount: partnerPlanRefunds > 0 ? partnerPlanRefunds : null,
            partner_fact_refunds_amount: partnerFactRefunds,
            studio_plan_gross_revenue: row.planGrossRevenue,
            studio_fact_gross_revenue: row.factGrossRevenue,
            studio_plan_marketing_budget: row.planMarketing,
            studio_fact_marketing_spent: row.factMarketing,
            studio_plan_leads_count: row.planLeads,
            studio_fact_leads_count: row.factLeads,
            studio_plan_calls_count: null,
            studio_fact_calls_count: row.factCalls,
            studio_plan_answered_calls_count: null,
            studio_fact_answered_calls_count: row.factAnsweredCalls,
            studio_plan_appointments_count: row.planAppointments,
            studio_fact_appointments_count: row.factAppointments,
            studio_plan_new_visitors_count: row.planVisitors,
            studio_fact_new_visitors_count: row.factVisitors,
            studio_plan_sales_count: row.planSalesCount,
            studio_fact_sales_count: row.factSalesCount,
            studio_plan_booking_conversion: row.planLeads > 0 ? (row.planAppointments / row.planLeads) * 100 : null,
            studio_fact_booking_conversion: row.factLeads > 0 ? (row.factAppointments / row.factLeads) * 100 : null,
            studio_plan_visit_conversion: row.planAppointments > 0 ? (row.planVisitors / row.planAppointments) * 100 : null,
            studio_fact_visit_conversion: row.factAppointments > 0 ? (row.factVisitors / row.factAppointments) * 100 : null,
            studio_plan_sales_conversion: row.planVisitors > 0 ? (row.planSalesCount / row.planVisitors) * 100 : null,
            studio_fact_sales_conversion: row.factVisitors > 0 ? (row.factSalesCount / row.factVisitors) * 100 : null,
            studio_plan_avg_check_new: row.planAvgCheckNew,
            studio_fact_avg_check_new: row.factAvgCheckNew,
            studio_plan_avg_check_existing: row.planAvgCheckExisting,
            studio_fact_avg_check_existing: row.factAvgCheckExisting,
            studio_plan_refunds_amount: row.planRefunds,
            studio_fact_refunds_amount: row.factRefunds,
          }))
        })

        if (!cancelled) setWeekDetailRows(nextRows)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setWeekLoading(false)
      }
    }

    buildWeekDetailRows()
    return () => { cancelled = true }
  }, [partnerDetailRows, periods.currentPeriod?.period_id, selectedRange])

  const currentPeriod = useMemo(() => {
    const selectedPeriodId = periods.currentPeriod?.period_id ?? null
    if (selectedPeriodId != null) {
      return periodTotals.find((period) => period.period_id === selectedPeriodId) ?? null
    }
    return periodTotals[0] ?? null
  }, [periodTotals, periods.currentPeriod?.period_id])

  const expected = useMemo(
    () => {
      if (selectedRange?.mode === 'week') return periodProgress(selectedRange.start, selectedRange.end)
      return currentPeriod ? periodProgress(currentPeriod.period_start, currentPeriod.period_end) : 0
    },
    [currentPeriod, selectedRange]
  )

  const effectiveDetailRows = useMemo(() => {
    if (selectedRange?.mode === 'week') {
      return weekDetailRows
    }
    // Use periodAvgCheckRows only if they contain all partners from partnerDetailRows
    // Otherwise stick with partnerDetailRows to avoid hiding partners
    if (periodAvgCheckRows.length > 0) {
      const partnerCountInAvg = new Set(periodAvgCheckRows.map(r => r.partner_number)).size
      const partnerCountInDetail = new Set(partnerDetailRows.map(r => r.partner_number)).size
      if (partnerCountInAvg >= partnerCountInDetail) {
        return periodAvgCheckRows
      }
    }
    return partnerDetailRows
  }, [partnerDetailRows, periodAvgCheckRows, selectedRange?.mode, weekDetailRows])

  const partnerCards = useMemo(() => {
    const map = new Map<number, {
      assignment_id: number
      partner_number: number
      cities_text: string | null
      persons_fio: string | null
      partner_studio_count: number
      assigned_from: string
      assigned_to: string | null
      auto_closed_by_role_end: boolean
      partner_currency_code: string | null
      partner_plan_gross_revenue: number
      partner_fact_gross_revenue: number
      partner_plan_marketing_budget: number
      partner_fact_marketing_spent: number
      partner_plan_leads_count: number
      partner_fact_leads_count: number
      partner_plan_calls_count: number | null
      partner_fact_calls_count: number
      partner_plan_answered_calls_count: number | null
      partner_fact_answered_calls_count: number | null
      partner_plan_sales_count: number
      partner_fact_sales_count: number
      partner_plan_sales_conversion: number | null
      partner_fact_sales_conversion: number | null
      partner_plan_booking_conversion: number | null
      partner_fact_booking_conversion: number | null
      partner_plan_visit_conversion: number | null
      partner_fact_visit_conversion: number | null
      partner_plan_avg_check_new: number | null
      partner_fact_avg_check_new: number | null
      partner_plan_avg_check_existing: number | null
      partner_fact_avg_check_existing: number | null
      partner_plan_refunds_amount: number | null
      partner_fact_refunds_amount: number
      studios: TerritorialManagerPartnerPeriodDetailRow[]
    }>()

    for (const row of effectiveDetailRows) {
      const existing = map.get(row.partner_number)
      if (!existing) {
        map.set(row.partner_number, {
          assignment_id: row.assignment_id,
          partner_number: row.partner_number,
          cities_text: row.cities_text,
          persons_fio: row.persons_fio,
          partner_studio_count: row.partner_studio_count,
          assigned_from: row.assigned_from,
          assigned_to: row.assigned_to,
          auto_closed_by_role_end: row.auto_closed_by_role_end,
          partner_currency_code: row.partner_currency_code,
          partner_plan_gross_revenue: row.partner_plan_gross_revenue,
          partner_fact_gross_revenue: row.partner_fact_gross_revenue,
          partner_plan_marketing_budget: row.partner_plan_marketing_budget,
          partner_fact_marketing_spent: row.partner_fact_marketing_spent,
          partner_plan_leads_count: row.partner_plan_leads_count,
          partner_fact_leads_count: row.partner_fact_leads_count,
          partner_plan_calls_count: row.partner_plan_calls_count,
          partner_fact_calls_count: row.partner_fact_calls_count,
          partner_plan_answered_calls_count: row.partner_plan_answered_calls_count,
          partner_fact_answered_calls_count: row.partner_fact_answered_calls_count,
          partner_plan_sales_count: row.partner_plan_sales_count,
          partner_fact_sales_count: row.partner_fact_sales_count,
          partner_plan_sales_conversion: row.partner_plan_sales_conversion,
          partner_fact_sales_conversion: row.partner_fact_sales_conversion,
          partner_plan_booking_conversion: row.partner_plan_booking_conversion,
          partner_fact_booking_conversion: row.partner_fact_booking_conversion,
          partner_plan_visit_conversion: row.partner_plan_visit_conversion,
          partner_fact_visit_conversion: row.partner_fact_visit_conversion,
          partner_plan_avg_check_new: row.partner_plan_avg_check_new,
          partner_fact_avg_check_new: row.partner_fact_avg_check_new,
          partner_plan_avg_check_existing: row.partner_plan_avg_check_existing,
          partner_fact_avg_check_existing: row.partner_fact_avg_check_existing,
          partner_plan_refunds_amount: row.partner_plan_refunds_amount,
          partner_fact_refunds_amount: row.partner_fact_refunds_amount,
          studios: row.studio_internal_id ? [row] : [],
        })
      } else if (row.studio_internal_id) {
        existing.studios.push(row)
      }
    }

    return Array.from(map.values())
  }, [effectiveDetailRows])

  // Enrich each partner with computed statuses + studio problem summary
  const enrichedPartners = useMemo(() => {
    return partnerCards.map(p => {
      const revStatus = metricStatus(p.partner_plan_gross_revenue, p.partner_fact_gross_revenue, expected)
      const marketingStatus = metricStatus(p.partner_plan_marketing_budget, p.partner_fact_marketing_spent, expected)
      const salesStatus = metricStatus(p.partner_plan_sales_count, p.partner_fact_sales_count, expected)
      const leadsStatus = metricStatus(p.partner_plan_leads_count, p.partner_fact_leads_count, expected)
      const callsStatus = metricStatus(p.partner_plan_calls_count, p.partner_fact_calls_count, expected)
      const answeredCallsStatus = metricStatus(p.partner_plan_answered_calls_count, p.partner_fact_answered_calls_count, expected)
      const cr1Status = rateStatus(p.partner_plan_booking_conversion, p.partner_fact_booking_conversion)
      const cr2Status = rateStatus(p.partner_plan_visit_conversion, p.partner_fact_visit_conversion)
      const cr3Status = rateStatus(p.partner_plan_sales_conversion, p.partner_fact_sales_conversion)
      const avgNewStatus = rateStatus(p.partner_plan_avg_check_new, p.partner_fact_avg_check_new)
      const avgExistingStatus = rateStatus(p.partner_plan_avg_check_existing, p.partner_fact_avg_check_existing)
      const refundsStatus = metricStatus(p.partner_plan_refunds_amount, p.partner_fact_refunds_amount, expected)

      const overall = [revStatus, marketingStatus, salesStatus].reduce(worseStatus, 'ok' as Status)

      const studioStatuses = p.studios.map(s => ({
        studio: s,
        status: metricStatus(s.studio_plan_gross_revenue, s.studio_fact_gross_revenue, expected),
      }))
      const problemStudios = studioStatuses.filter(s => s.status === 'bad' || s.status === 'warn').length
      const noPlanStudios = studioStatuses.filter(s => s.status === 'no-plan').length

      // deviation in percentage points vs expected baseline (negative = behind)
      const factPct = p.partner_plan_gross_revenue > 0
        ? p.partner_fact_gross_revenue / p.partner_plan_gross_revenue
        : null
      const deviation = factPct != null ? (factPct - expected) : -Infinity

      return {
        ...p,
        revStatus,
        marketingStatus,
        salesStatus,
        leadsStatus,
        callsStatus,
        answeredCallsStatus,
        cr1Status,
        cr2Status,
        cr3Status,
        avgNewStatus,
        avgExistingStatus,
        refundsStatus,
        overall,
        studioStatuses,
        problemStudios,
        noPlanStudios,
        deviation,
        factPct,
      }
    })
  }, [partnerCards, expected])

  const partnerWeekly = useMemo(() => {
    const byAssignment = new Map<number, TerritorialManagerWeeklyRevenueRow[]>()
    for (const r of weeklyRevenueRows) {
      const list = byAssignment.get(r.assignment_id) ?? []
      list.push(r)
      byAssignment.set(r.assignment_id, list)
    }
    const out = new Map<number, WeeklyAggregate[]>()
    for (const [aid, rows] of byAssignment) out.set(aid, aggregateWeeks(rows))
    return out
  }, [weeklyRevenueRows])
  const managerWeekly = useMemo<WeeklyAggregate[]>(
    () => aggregateWeeks(weeklyRevenueRows),
    [weeklyRevenueRows]
  )
  const currentWeekIndex = useMemo(() => {
    const t = todayISO()
    return managerWeekly.findIndex(w => t >= w.start && t <= w.end)
  }, [managerWeekly])

  const filteredSortedPartners = useMemo(() => {
    let arr = enrichedPartners

    const q = search.trim().toLowerCase()
    if (q) {
      arr = arr.filter(p =>
        String(p.partner_number).includes(q) ||
        (p.persons_fio?.toLowerCase().includes(q) ?? false) ||
        (p.cities_text?.toLowerCase().includes(q) ?? false)
      )
    }

    switch (filter) {
      case 'problems':
        arr = arr.filter(p => p.overall === 'bad' || p.overall === 'warn')
        break
      case 'no-plan':
        arr = arr.filter(p => p.revStatus === 'no-plan' || p.noPlanStudios > 0)
        break
      case 'on-track':
        arr = arr.filter(p => p.overall === 'ok')
        break
    }

    const sorted = [...arr]
    switch (sortKey) {
      case 'deviation':
        sorted.sort((a, b) => a.deviation - b.deviation) // worst first
        break
      case 'revenue':
        sorted.sort((a, b) => b.partner_fact_gross_revenue - a.partner_fact_gross_revenue)
        break
      case 'name':
        sorted.sort((a, b) => (a.persons_fio ?? '').localeCompare(b.persons_fio ?? '') || a.partner_number - b.partner_number)
        break
    }
    return sorted
  }, [enrichedPartners, filter, sortKey, search])

  const summary = useMemo(() => {
    const total = enrichedPartners.length
    const problems = enrichedPartners.filter(p => p.overall === 'bad' || p.overall === 'warn').length
    const noPlan = enrichedPartners.filter(p => p.revStatus === 'no-plan' || p.noPlanStudios > 0).length
    const onTrack = enrichedPartners.filter(p => p.overall === 'ok').length
    return { total, problems, noPlan, onTrack }
  }, [enrichedPartners])

  // Aggregated portfolio metrics for the top summary block. Built from studio rows so we have
  // appointments / visitors (not exposed at the period-totals view) and weighted avg checks.
  const portfolio = useMemo(() => {
    let revPlan = 0, revFact = 0
    let marketingPlan = 0, marketingFact = 0
    let leadsPlan = 0, leadsFact = 0
    let callsFact = 0, answeredCallsFact = 0
    let salesPlan = 0, salesFact = 0
    let refundsFact = 0
    let apptPlan = 0, apptFact = 0
    let visitPlan = 0, visitFact = 0
    let avgNewPlanSum = 0, avgNewPlanW = 0, avgNewFactSum = 0, avgNewFactW = 0
    let avgExPlanSum = 0, avgExPlanW = 0, avgExFactSum = 0, avgExFactW = 0

    for (const r of effectiveDetailRows) {
      if (r.studio_internal_id == null) continue
      revPlan += r.studio_plan_gross_revenue
      revFact += r.studio_fact_gross_revenue
      marketingPlan += r.studio_plan_marketing_budget
      marketingFact += r.studio_fact_marketing_spent
      leadsPlan += r.studio_plan_leads_count
      leadsFact += r.studio_fact_leads_count
      callsFact += r.studio_fact_calls_count
      answeredCallsFact += r.studio_fact_answered_calls_count ?? 0
      salesPlan += r.studio_plan_sales_count
      salesFact += r.studio_fact_sales_count
      refundsFact += r.studio_fact_refunds_amount
      apptPlan += r.studio_plan_appointments_count
      apptFact += r.studio_fact_appointments_count
      visitPlan += r.studio_plan_new_visitors_count
      visitFact += r.studio_fact_new_visitors_count

      if (r.studio_plan_avg_check_new != null && r.studio_plan_sales_count > 0) {
        avgNewPlanSum += r.studio_plan_avg_check_new * r.studio_plan_sales_count
        avgNewPlanW += r.studio_plan_sales_count
      }
      if (r.studio_fact_avg_check_new != null && r.studio_fact_sales_count > 0) {
        avgNewFactSum += r.studio_fact_avg_check_new * r.studio_fact_sales_count
        avgNewFactW += r.studio_fact_sales_count
      }
      if (r.studio_plan_avg_check_existing != null && r.studio_plan_sales_count > 0) {
        avgExPlanSum += r.studio_plan_avg_check_existing * r.studio_plan_sales_count
        avgExPlanW += r.studio_plan_sales_count
      }
      if (r.studio_fact_avg_check_existing != null && r.studio_fact_sales_count > 0) {
        avgExFactSum += r.studio_fact_avg_check_existing * r.studio_fact_sales_count
        avgExFactW += r.studio_fact_sales_count
      }
    }

    const pct = (n: number, d: number) => d > 0 ? (n / d) * 100 : 0
    return {
      rev: { plan: revPlan, fact: revFact },
      marketing: { plan: marketingPlan, fact: marketingFact },
      leads: { plan: leadsPlan, fact: leadsFact },
      calls: { plan: null, fact: callsFact },
      answeredCalls: { plan: null, fact: answeredCallsFact },
      sales: { plan: salesPlan, fact: salesFact },
      cr1: { plan: pct(apptPlan, leadsPlan), fact: pct(apptFact, leadsFact) },
      cr2: { plan: pct(visitPlan, apptPlan), fact: pct(visitFact, apptFact) },
      cr3: { plan: pct(salesPlan, visitPlan), fact: pct(salesFact, visitFact) },
      avgNew: {
        plan: avgNewPlanW > 0 ? avgNewPlanSum / avgNewPlanW : 0,
        fact: avgNewFactW > 0 ? avgNewFactSum / avgNewFactW : 0,
      },
      avgExisting: {
        plan: avgExPlanW > 0 ? avgExPlanSum / avgExPlanW : 0,
        fact: avgExFactW > 0 ? avgExFactSum / avgExFactW : 0,
      },
      refunds: { plan: null, fact: refundsFact },
    }
  }, [effectiveDetailRows])

  const portfolioMoneyByCurrency = useMemo(() => {
    const map = new Map<string, {
      revenuePlan: number
      revenueFact: number
      marketingPlan: number
      marketingFact: number
      refundsPlan: number
      refundsFact: number
      avgNewPlanSum: number
      avgNewPlanWeight: number
      avgNewFactSum: number
      avgNewFactWeight: number
      avgExistingPlanSum: number
      avgExistingPlanWeight: number
      avgExistingFactSum: number
      avgExistingFactWeight: number
    }>()

    for (const r of effectiveDetailRows) {
      if (r.studio_internal_id == null) continue
      const currency = r.studio_currency_code || '—'
      const bucket = map.get(currency) ?? {
        revenuePlan: 0,
        revenueFact: 0,
        marketingPlan: 0,
        marketingFact: 0,
        refundsPlan: 0,
        refundsFact: 0,
        avgNewPlanSum: 0,
        avgNewPlanWeight: 0,
        avgNewFactSum: 0,
        avgNewFactWeight: 0,
        avgExistingPlanSum: 0,
        avgExistingPlanWeight: 0,
        avgExistingFactSum: 0,
        avgExistingFactWeight: 0,
      }

      bucket.revenuePlan += r.studio_plan_gross_revenue || 0
      bucket.revenueFact += r.studio_fact_gross_revenue || 0
      bucket.marketingPlan += r.studio_plan_marketing_budget || 0
      bucket.marketingFact += r.studio_fact_marketing_spent || 0
      bucket.refundsPlan += r.studio_plan_refunds_amount || 0
      bucket.refundsFact += r.studio_fact_refunds_amount || 0

      if (r.studio_plan_avg_check_new != null && (r.studio_plan_sales_count || 0) > 0) {
        bucket.avgNewPlanSum += r.studio_plan_avg_check_new * (r.studio_plan_sales_count || 0)
        bucket.avgNewPlanWeight += r.studio_plan_sales_count || 0
      }
      if (r.studio_fact_avg_check_new != null && (r.studio_fact_sales_count || 0) > 0) {
        bucket.avgNewFactSum += r.studio_fact_avg_check_new * (r.studio_fact_sales_count || 0)
        bucket.avgNewFactWeight += r.studio_fact_sales_count || 0
      }
      if (r.studio_plan_avg_check_existing != null && (r.studio_plan_sales_count || 0) > 0) {
        bucket.avgExistingPlanSum += r.studio_plan_avg_check_existing * (r.studio_plan_sales_count || 0)
        bucket.avgExistingPlanWeight += r.studio_plan_sales_count || 0
      }
      if (r.studio_fact_avg_check_existing != null && (r.studio_fact_sales_count || 0) > 0) {
        bucket.avgExistingFactSum += r.studio_fact_avg_check_existing * (r.studio_fact_sales_count || 0)
        bucket.avgExistingFactWeight += r.studio_fact_sales_count || 0
      }

      map.set(currency, bucket)
    }

    return Array.from(map.entries())
      .map(([currency, bucket]) => ({
        currency,
        revenue: { plan: bucket.revenuePlan, fact: bucket.revenueFact },
        marketing: { plan: bucket.marketingPlan, fact: bucket.marketingFact },
        refunds: {
          plan: bucket.refundsPlan > 0 ? bucket.refundsPlan : null,
          fact: bucket.refundsFact,
        },
        avgNew: {
          plan: bucket.avgNewPlanWeight > 0 ? bucket.avgNewPlanSum / bucket.avgNewPlanWeight : null,
          fact: bucket.avgNewFactWeight > 0 ? bucket.avgNewFactSum / bucket.avgNewFactWeight : null,
        },
        avgExisting: {
          plan: bucket.avgExistingPlanWeight > 0 ? bucket.avgExistingPlanSum / bucket.avgExistingPlanWeight : null,
          fact: bucket.avgExistingFactWeight > 0 ? bucket.avgExistingFactSum / bucket.avgExistingFactWeight : null,
        },
      }))
      .sort((a, b) => a.currency.localeCompare(b.currency))
  }, [effectiveDetailRows])

  // Последний день в должности ТУ, включительно (tu_role_end_date). Новый ТУ — со следующего дня.
  const roleLastDay = managerInfo?.tu_role_end_date ?? null
  const isRoleRemovalScheduled = !!roleLastDay && roleLastDay >= todayISO()
  const isFormerTu = !!managerInfo && (
    managerInfo.job_title !== 'territorial_manager' || (!!roleLastDay && roleLastDay < todayISO())
  )

  // Предпросмотр: что закроется и удалится при выбранной дате, и не наедет ли перенос на нового ТУ.
  useEffect(() => {
    if (!removeTitleOpen || !managerInfo || !removeTitleDate) {
      setRemovePreview(null)
      setRemovePreviewError(null)
      return
    }
    let cancelled = false
    // Предпросмотр прежней даты сразу снимаем: подтверждать можно только то, что оператор видел.
    setRemovePreview(null)
    setRemovePreviewError(null)
    const t = setTimeout(() => {
      setTerritorialManagerLastDay({
        userId: managerInfo.manager_user_id,
        lastDay: removeTitleDate,
        dryRun: true,
      })
        .then(r => { if (!cancelled) setRemovePreview(r) })
        .catch(err => {
          if (cancelled) return
          setRemovePreview(null)
          setRemovePreviewError(err instanceof Error ? err.message : String(err))
        })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [removeTitleOpen, removeTitleDate, managerInfo])

  const removeTitleActionLabel = (() => {
    if (roleLastDay) return 'Сохранить дату'
    return removeTitleDate >= todayISO() ? 'Запланировать снятие' : 'Снять'
  })()

  // Кнопка доступна, только когда предпросмотр посчитан ровно для выбранной даты.
  const removePreviewReady = !!removePreview && removePreview.last_day === removeTitleDate

  const handleRemoveTitleSubmit = async () => {
    if (!managerInfo || !removeTitleDate || !removePreviewReady) return
    const parts = [
      roleLastDay
        ? `Изменить последний день в должности ТУ: ${fmtDate(roleLastDay)} → ${fmtDate(removeTitleDate)}?`
        : `Снять с должности ТУ: последний день ${fmtDate(removeTitleDate)} включительно?`,
      `Нового ТУ франчайзи можно будет назначить с ${fmtDate(dayAfter(removeTitleDate))}.`,
    ]
    if (removePreview?.closed) parts.push(`Назначений будет закрыто/перенесено: ${removePreview.closed}.`)
    if (removePreview?.deleted) {
      parts.push(`Удалятся назначения, начатые после ${fmtDate(removeTitleDate)}: ${removePreview.deleted_list.join('; ')}.`)
    }
    if (!confirm(parts.join('\n'))) return
    setActionError(null)
    setRemoveBusy(true)
    try {
      await setTerritorialManagerLastDay({
        userId: managerInfo.manager_user_id,
        lastDay: removeTitleDate,
      })
      setRemoveTitleOpen(false)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setRemoveBusy(false)
    }
  }

  const handleCancelScheduledTuRemoval = async () => {
    if (!managerInfo?.tu_role_end_date) return
    if (!confirm(`Отменить запланированное снятие с должности ТУ (последний день ${fmtDate(managerInfo.tu_role_end_date)})? Назначения, закрытые этой датой, снова станут бессрочными.`)) return
    setActionError(null)
    try {
      await cancelScheduledTuRemoval(managerInfo.manager_user_id)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    }
  }

  /**
   * Подтверждение снятия. Печатаем ОБЕ даты — введённую («снят с») и то, что
   * реально ляжет в назначение («числится по»): раньше подтверждение говорило
   * «сегодня» при любой дате ≤ сегодня, и оператор не видел, что вводимая дата
   * вообще на что-то влияет.
   */
  const unassignConfirmText = (partnerNumber: number, date: string) => {
    const lastDay = lastDayBefore(date)
    const when =
      date > todayISO() ? 'Запланировать снятие' : date === todayISO() ? 'Снять' : 'Снять задним числом'
    return `${when} франчайзи (${partnerNumber}) с ${fmtDate(date)}?\n\nФранчайзи останется за ТУ по ${fmtDate(lastDay)} включительно.`
  }

  const handleUnassignSubmit = async (p: { assignment_id: number; partner_number: number; assigned_from: string }) => {
    if (unassignDate <= p.assigned_from) {
      // Пустой интервал — сервер в этом случае удаляет назначение целиком.
      if (
        !confirm(
          `Дата снятия ${fmtDate(unassignDate)} не позже начала назначения (${fmtDate(p.assigned_from)}).\n\n` +
            `Назначение франчайзи (${p.partner_number}) будет удалено полностью, как будто его не было. Продолжить?`
        )
      )
        return
    } else if (!confirm(unassignConfirmText(p.partner_number, unassignDate))) {
      return
    }
    setBusyId(p.assignment_id)
    setActionError(null)
    try {
      await unassignTerritorialManager({ assignmentId: p.assignment_id, unassignedAt: unassignDate })
      setUnassignFormId(null)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const handleClosedDateSubmit = async (a: TerritorialManagerClosedAssignment) => {
    if (closedDate <= a.assigned_from) {
      if (
        !confirm(
          `Дата снятия ${fmtDate(closedDate)} не позже начала назначения (${fmtDate(a.assigned_from)}).\n\n` +
            `Назначение франчайзи (${a.partner_number}) будет удалено полностью. Продолжить?`
        )
      )
        return
    } else if (!confirm(unassignConfirmText(a.partner_number, closedDate))) {
      return
    }
    setBusyId(a.assignment_id)
    setActionError(null)
    try {
      await unassignTerritorialManager({ assignmentId: a.assignment_id, unassignedAt: closedDate })
      setClosedFormId(null)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const handleReopenClosed = async (a: TerritorialManagerClosedAssignment) => {
    if (
      !confirm(
        `Вернуть франчайзи (${a.partner_number}) этому ТУ?\n\n` +
          `Назначение снова станет открытым, снятие с ${fmtDate(a.unassigned_at)} будет отменено.`
      )
    )
      return
    setBusyId(a.assignment_id)
    setActionError(null)
    try {
      await cancelScheduledAssignmentClose(a.assignment_id)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const handleCancelAssignmentClose = async (p: { assignment_id: number; partner_number: number; assigned_to: string | null }) => {
    if (!p.assigned_to) return
    if (
      !confirm(
        `Отменить запланированное снятие франчайзи (${p.partner_number}) с ${fmtDate(dayAfter(p.assigned_to))}?`
      )
    )
      return
    setBusyId(p.assignment_id)
    setActionError(null)
    try {
      await cancelScheduledAssignmentClose(p.assignment_id)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const togglePartnerExpanded = (assignmentId: number) => {
    setExpandedPartnerIds(prev =>
      prev.includes(assignmentId)
        ? prev.filter(id => id !== assignmentId)
        : [...prev, assignmentId]
    )
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate('/management/territorial-managers')}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} /> К списку ТУ
      </button>

      {periods.currentPeriod && (
        <Card className="py-2">
          <PeriodSelector
            currentPeriod={periods.currentPeriod}
            onPrevPeriod={periods.goToPrevPeriod}
            onNextPeriod={periods.goToNextPeriod}
            canGoPrev={periods.canGoPrev}
            canGoNext={periods.canGoNext}
            showViewModeSelector
            isCurrentPeriod={periods.isCurrentPeriod}
            periodFilterValue={selectedTimeFilter}
            periodFilterOptions={timeFilterOptions}
            onPeriodFilterChange={setSelectedTimeFilter}
            selectedRange={selectedRange}
            compact
          />
        </Card>
      )}

      {(loading || periods.loading || weekLoading) && <Card><div className="text-sm text-muted-foreground">Загрузка…</div></Card>}
      {periods.error && <Card><div className="text-sm text-destructive">Ошибка периодов: {periods.error}</div></Card>}
      {error && <Card><div className="text-sm text-destructive">Ошибка: {error}</div></Card>}

      {!loading && !periods.loading && !periods.error && !error && managerInfo && (
        <>
          {/* Manager header */}
          <Card className="!p-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-base font-semibold truncate min-w-0">
                {managerInfo.manager_name || `Пользователь #${userId.slice(0, 8)}`}
              </h1>
              {managerInfo.manager_user_id === user?.id && (
                <Badge text="Вы" variant="default" />
              )}
              {isRoleRemovalScheduled && roleLastDay && (
                <Badge text={`в должности по ${fmtDate(roleLastDay)}`} variant="warning" />
              )}
              {isFormerTu && (
                <Badge
                  text={roleLastDay ? `снят(а) с должности, последний день ${fmtDate(roleLastDay)}` : 'снят(а) с должности ТУ, дата не записана'}
                  variant="neutral"
                />
              )}
              {managerInfo.tg_username && (
                <span className="text-xs text-muted-foreground">@{managerInfo.tg_username}</span>
              )}
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1">
                  <Users size={14} className="text-muted-foreground" />
                  <span className="font-semibold">{partnerCards.length}</span>
                  <span className="text-muted-foreground">франч.</span>
                </div>
                <div className="flex items-center gap-1">
                  <Building2 size={14} className="text-muted-foreground" />
                  <span className="font-semibold">{managerInfo.studios_count}</span>
                  <span className="text-muted-foreground">студ.</span>
                </div>
              </div>

              {isAdvanced && (
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {!removeTitleOpen ? (
                    <>
                      {isRoleRemovalScheduled && (
                        <ActionButton onClick={handleCancelScheduledTuRemoval}>
                          Отменить снятие
                        </ActionButton>
                      )}
                      <ActionButton
                        onClick={() => {
                          setRemoveTitleDate(roleLastDay ?? lastDayBefore(todayISO()))
                          setRemoveTitleOpen(true)
                        }}
                      >
                        {roleLastDay
                          ? 'Изменить дату снятия'
                          : isFormerTu ? 'Указать дату снятия' : 'Снять с должности ТУ'}
                      </ActionButton>
                    </>
                  ) : (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Calendar size={14} className="text-muted-foreground" />
                        <label className="text-xs text-muted-foreground" htmlFor="tu-removal-date">
                          Последний день в должности
                        </label>
                        <input
                          id="tu-removal-date"
                          type="date"
                          value={removeTitleDate}
                          onChange={e => setRemoveTitleDate(e.target.value)}
                          className="px-2 py-1 border border-border rounded text-sm bg-background"
                        />
                        <ActionButton
                          tone="primary"
                          disabled={removeBusy || !removeTitleDate || !removePreviewReady || !!removePreview?.conflict || !!removePreviewError}
                          onClick={handleRemoveTitleSubmit}
                        >
                          {removeTitleActionLabel}
                        </ActionButton>
                        <ActionButton onClick={() => setRemoveTitleOpen(false)}>
                          Отмена
                        </ActionButton>
                      </div>
                      {removeTitleDate && (
                        <div className="text-xs text-muted-foreground text-right max-w-xl">
                          По {fmtDate(removeTitleDate)} включительно франчайзи числятся за этим ТУ, нового можно назначить с {fmtDate(dayAfter(removeTitleDate))}.
                          {removePreview && removePreview.closed > 0 && ` Назначений закроется/перенесётся: ${removePreview.closed}.`}
                          {removePreview && removePreview.deleted > 0 && ` Удалятся (начаты после этой даты): ${removePreview.deleted_list.join('; ')}.`}
                        </div>
                      )}
                      {removePreview?.conflict && (
                        <div className="text-xs text-destructive text-right max-w-xl">
                          {removePreview.conflict} {removePreview.conflict_hint}
                        </div>
                      )}
                      {removePreviewError && (
                        <div className="text-xs text-destructive text-right max-w-xl">{removePreviewError}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Period summary — compact */}
          {currentPeriod && (
            <Card className="!p-3">
              <div className="flex items-stretch gap-3 flex-wrap">
                <div className="min-w-0 flex-shrink-0">
                  <div className="text-xs text-muted-foreground">{selectedRange?.mode === 'week' ? 'Неделя' : 'Период'}</div>
                  <div className="text-sm font-semibold whitespace-nowrap">
                    {selectedRange?.mode === 'week' ? selectedRange.label : currentPeriod.month_year}
                  </div>
                </div>
                <div className="basis-[260px] grow-0 shrink-0 flex flex-col justify-center">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                    <span>{selectedRange?.start || currentPeriod.period_start} → {selectedRange?.end || currentPeriod.period_end}</span>
                    <span className="font-medium text-foreground">{formatPct(expected * 100)} прошло</span>
                  </div>
                  <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary/70 transition-all" style={{ width: `${expected * 100}%` }} />
                  </div>
                </div>
                {managerWeekly.length > 0 && (
                  <div
                    className="flex-1 min-w-[320px] grid gap-1.5"
                    style={{ gridTemplateColumns: `repeat(${managerWeekly.length}, minmax(0, 1fr))` }}
                  >
                    {managerWeekly.map((w, i) => {
                      const pct = w.plan != null && w.plan > 0 && w.fact != null ? (w.fact / w.plan) * 100 : null
                      return (
                        <div
                          key={w.n}
                          title={`Н${w.n}: ${w.start} → ${w.end}`}
                          className={`rounded-md border bg-background p-2 ${
                            i === currentWeekIndex ? 'border-foreground/50' : 'border-border/70'
                          }`}
                        >
                          <div className="text-xs tabular-nums leading-tight">
                            <span className={`font-semibold ${statusTextColor(w.status)}`}>
                              {w.fact != null ? formatCurrency(w.fact, 'RUB') : '—'}
                            </span>
                          </div>
                          <div className="text-[10px] text-muted-foreground tabular-nums leading-tight">
                            план {w.plan != null ? formatCurrency(w.plan, 'RUB') : '—'}
                          </div>
                          <div className="mt-1.5 flex items-center gap-1.5">
                            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                              <div
                                className={statusStripe(w.status)}
                                style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%`, height: '100%' }}
                              />
                            </div>
                            <span className={`text-[10px] font-medium tabular-nums ${statusTextColor(w.status)}`}>
                              {pct == null ? '—' : formatPct(pct)}
                            </span>
                          </div>
                          <div className="mt-1 text-[10px] text-muted-foreground leading-tight">
                            {fmtDayMonth(w.start)} → {fmtDayMonth(w.end)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-1.5">
                <MoneySummaryMetric
                  label="Выручка"
                  rows={portfolioMoneyByCurrency.map((row) => ({
                    currency: row.currency,
                    plan: row.revenue.plan,
                    fact: row.revenue.fact,
                  }))}
                  expected={expected}
                />
                <MoneySummaryMetric
                  label="Маркетинг"
                  rows={portfolioMoneyByCurrency.map((row) => ({
                    currency: row.currency,
                    plan: row.marketing.plan,
                    fact: row.marketing.fact,
                  }))}
                  expected={expected}
                />
                <SummaryMetric
                  label="Лиды"
                  plan={portfolio.leads.plan}
                  fact={portfolio.leads.fact}
                  expected={expected}
                />
                <SummaryMetric
                  label="Звонки"
                  plan={portfolio.calls.plan}
                  fact={portfolio.calls.fact}
                  expected={1}
                />
                <SummaryMetric
                  label="Дозвоны"
                  plan={portfolio.answeredCalls.plan}
                  fact={portfolio.answeredCalls.fact}
                  expected={1}
                />
                <SummaryMetric
                  label="Продажи новым"
                  plan={portfolio.sales.plan}
                  fact={portfolio.sales.fact}
                  expected={expected}
                />
                <SummaryMetric
                  label="CR1 лид → запись"
                  plan={portfolio.cr1.plan}
                  fact={portfolio.cr1.fact}
                  expected={1}
                  formatter={formatPercentage}
                />
                <SummaryMetric
                  label="CR2 запись → приход"
                  plan={portfolio.cr2.plan}
                  fact={portfolio.cr2.fact}
                  expected={1}
                  formatter={formatPercentage}
                />
                <SummaryMetric
                  label="CR3 приход → продажа"
                  plan={portfolio.cr3.plan}
                  fact={portfolio.cr3.fact}
                  expected={1}
                  formatter={formatPercentage}
                />
                <MoneySummaryMetric
                  label="Ср. чек новые"
                  rows={portfolioMoneyByCurrency.map((row) => ({
                    currency: row.currency,
                    plan: row.avgNew.plan,
                    fact: row.avgNew.fact,
                  }))}
                  expected={1}
                  isAverage
                />
                <MoneySummaryMetric
                  label="Ср. чек постоянные"
                  rows={portfolioMoneyByCurrency.map((row) => ({
                    currency: row.currency,
                    plan: row.avgExisting.plan,
                    fact: row.avgExisting.fact,
                  }))}
                  expected={1}
                  isAverage
                />
                <MoneySummaryMetric
                  label="Возвраты"
                  rows={portfolioMoneyByCurrency.map((row) => ({
                    currency: row.currency,
                    plan: row.refunds.plan,
                    fact: row.refunds.fact,
                  }))}
                  expected={1}
                />
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                Денежные сводные карточки показаны по локальным валютам отдельными строками.
              </div>
            </Card>
          )}

          {actionError && (
            <Card className="border border-destructive/40">
              <div className="text-sm text-destructive flex items-center gap-2">
                <AlertTriangle size={14} /> {actionError}
              </div>
            </Card>
          )}

          {/* Filter / sort toolbar */}
          <Card className="!p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Поиск по франчайзи или городу"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 border border-border rounded-md text-sm bg-background"
                />
              </div>
              <select
                value={sortKey}
                onChange={e => setSortKey(e.target.value as SortKey)}
                className="px-2 py-1.5 border border-border rounded-md text-sm bg-background"
              >
                <option value="deviation">Сортировка: отставание</option>
                <option value="revenue">Сортировка: выручка</option>
                <option value="name">Сортировка: имя</option>
              </select>
            </div>
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label={`Все ${summary.total}`} />
              <FilterChip
                active={filter === 'problems'}
                onClick={() => setFilter('problems')}
                label={`Проблемы ${summary.problems}`}
                color="error"
              />
              <FilterChip
                active={filter === 'no-plan'}
                onClick={() => setFilter('no-plan')}
                label={`Без плана ${summary.noPlan}`}
                color="muted"
              />
              <FilterChip
                active={filter === 'on-track'}
                onClick={() => setFilter('on-track')}
                label={`По плану ${summary.onTrack}`}
                color="success"
              />
            </div>
          </Card>

          {/* Partners list — compact, one row per partner */}
          {partnerCards.length === 0 ? (
            <Card>
              <div className="text-sm text-muted-foreground">Активных назначений нет.</div>
            </Card>
          ) : filteredSortedPartners.length === 0 ? (
            <Card>
              <div className="text-sm text-muted-foreground">Ничего не найдено по выбранным фильтрам.</div>
            </Card>
          ) : (
            <div className="bg-card rounded-card shadow-card overflow-hidden divide-y divide-border/60">
              {/* Header */}
              <div className="hidden sm:flex items-center px-2 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/30">
                <div className="w-1 shrink-0" />
                <div className="flex-1 min-w-0 pl-2">Франчайзи</div>
                <div className="shrink-0 grid grid-cols-4 sm:grid-cols-12 gap-x-3 sm:gap-x-4 gap-y-0">
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Выручка</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Маркетинг</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Лиды</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Звонки</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Дозвоны</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Продажи</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">CR1</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">CR2</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">CR3</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Чек нов</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Чек пост</span>
                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Возвраты</span>
                </div>
                <div className="w-10 text-right pr-1 ml-2">Студ.</div>
                <div className="w-5" />
              </div>

              {filteredSortedPartners.map(p => {
                const scheduledClose = p.assigned_to && p.assigned_to > todayISO()
                const isFormOpen = unassignFormId === p.assignment_id
                const isExpanded = expandedPartnerIds.includes(p.assignment_id)
                const partnerWeeks = partnerWeekly.get(p.assignment_id) ?? []

                return (
                  <div key={p.assignment_id}>
                    {/* Single-line row */}
                    <button
                      type="button"
                      onClick={() => togglePartnerExpanded(p.assignment_id)}
                      className="w-full flex items-center text-left hover:bg-muted/30 transition"
                    >
                      <div className={`w-1 self-stretch shrink-0 ${statusStripe(p.overall)}`} />
                      <div className="flex-1 min-w-0 flex items-center gap-1.5 pl-2 pr-1 py-2">
                        <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${statusDot(p.overall)}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">
                            №{p.partner_number}{p.persons_fio ? ` · ${p.persons_fio}` : ''}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                            {p.cities_text && <span className="truncate">{p.cities_text}</span>}
                            {p.problemStudios > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-error shrink-0">
                                <AlertTriangle size={10} />{p.problemStudios}
                              </span>
                            )}
                            {p.noPlanStudios > 0 && (
                              <span className="inline-flex items-center gap-0.5 shrink-0">
                                <CircleDashed size={10} />{p.noPlanStudios}
                              </span>
                            )}
                            {scheduledClose && p.assigned_to && (
                              // assigned_to — последний день работы; снятие наступает днём позже.
                              <span
                                className="text-primary shrink-0"
                                title={`Числится по ${fmtDate(p.assigned_to)} включительно`}
                              >
                                ↘ снятие с {fmtDate(dayAfter(p.assigned_to))}
                              </span>
                            )}
                          </div>
                        </div>
                        {partnerWeeks.length > 0 && (
                          <div
                            className="hidden sm:flex items-end gap-0.5 shrink-0"
                            title="Выручка по неделям периода"
                          >
                            {partnerWeeks.map((w, i) => {
                              const ratio = w.plan != null && w.plan > 0 && w.fact != null ? w.fact / w.plan : 0
                              return (
                                <span
                                  key={w.n}
                                  className={`inline-block w-1.5 rounded-sm ${statusDot(w.status)} ${
                                    i === currentWeekIndex ? 'ring-1 ring-foreground/50' : ''
                                  }`}
                                  style={{ height: `${10 + Math.min(14, Math.round(ratio * 14))}px` }}
                                />
                              )
                            })}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 grid grid-cols-4 sm:grid-cols-12 gap-x-3 sm:gap-x-4 gap-y-0">
                        <MiniMetric fact={p.partner_fact_gross_revenue} plan={p.partner_plan_gross_revenue} status={p.revStatus} />
                        <MiniMetric fact={p.partner_fact_marketing_spent} plan={p.partner_plan_marketing_budget} status={p.marketingStatus} />
                        <MiniMetric fact={p.partner_fact_leads_count} plan={p.partner_plan_leads_count} status={p.leadsStatus} />
                        <MiniMetric fact={p.partner_fact_calls_count} plan={p.partner_plan_calls_count} status={p.callsStatus} />
                        <MiniMetric fact={p.partner_fact_answered_calls_count} plan={p.partner_plan_answered_calls_count} status={p.answeredCallsStatus} />
                        <MiniMetric fact={p.partner_fact_sales_count} plan={p.partner_plan_sales_count} status={p.salesStatus} />
                        <MiniMetric fact={p.partner_fact_booking_conversion} plan={p.partner_plan_booking_conversion} status={p.cr1Status} isRate />
                        <MiniMetric fact={p.partner_fact_visit_conversion} plan={p.partner_plan_visit_conversion} status={p.cr2Status} isRate />
                        <MiniMetric fact={p.partner_fact_sales_conversion} plan={p.partner_plan_sales_conversion} status={p.cr3Status} isRate />
                        <MiniMetric fact={p.partner_fact_avg_check_new} plan={p.partner_plan_avg_check_new} status={p.avgNewStatus} />
                        <MiniMetric fact={p.partner_fact_avg_check_existing} plan={p.partner_plan_avg_check_existing} status={p.avgExistingStatus} />
                        <MiniMetric fact={p.partner_fact_refunds_amount} plan={p.partner_plan_refunds_amount} status={p.refundsStatus} />
                      </div>
                      <div className="w-10 text-right text-xs text-muted-foreground tabular-nums shrink-0 pr-1 ml-2">
                        {p.partner_studio_count}
                      </div>
                      <div className="w-5 shrink-0 text-muted-foreground">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                    </button>

                    {/* Expanded section: actions + studios */}
                    {isExpanded && (
                      <div className="bg-muted/20 border-t border-border/60 px-2 sm:px-3 pt-2 pb-4 space-y-2">
                        {/* Actions + weekly breakdown */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <ActionButton
                              tone="primary"
                              icon={<ArrowUpRight size={13} />}
                              onClick={(e) => { e.stopPropagation(); navigate(`/partners/${p.partner_number}/analytics`) }}
                            >
                              Аналитика франчайзи
                            </ActionButton>
                            <span className="text-[11px] text-muted-foreground">С {p.assigned_from}</span>
                            {isAdvanced && scheduledClose && (
                              <ActionButton
                                icon={<X size={13} />}
                                disabled={busyId === p.assignment_id}
                                onClick={() => handleCancelAssignmentClose(p)}
                                title="Отменить запланированное снятие"
                              >
                                Отменить снятие
                              </ActionButton>
                            )}
                            {isAdvanced && !isFormOpen && (
                              <ActionButton
                                disabled={busyId === p.assignment_id}
                                onClick={() => {
                                  // У запланированного снятия подставляем уже назначенную
                                  // дату, а не «сегодня»: «Изменить дату» должна открываться
                                  // на текущем состоянии, иначе правка молча сдвинет срок.
                                  setUnassignDate(p.assigned_to ? dayAfter(p.assigned_to) : todayISO())
                                  setUnassignFormId(p.assignment_id)
                                }}
                              >
                                {scheduledClose ? 'Изменить дату' : 'Снять'}
                              </ActionButton>
                            )}
                          </div>

                          {partnerWeeks.length > 0 && (
                            <div
                              className="flex-1 grid gap-1.5"
                              style={{ gridTemplateColumns: `repeat(${partnerWeeks.length}, minmax(188px, 1fr))` }}
                            >
                              {partnerWeeks.map((w, i) => {
                                const pct = w.plan != null && w.plan > 0 && w.fact != null ? (w.fact / w.plan) * 100 : null
                                return (
                                  <div
                                    key={w.n}
                                    title={`Н${w.n}: ${w.start} → ${w.end}`}
                                    className={`rounded-md border bg-background p-1.5 ${
                                      i === currentWeekIndex ? 'border-foreground/50' : 'border-border/70'
                                    }`}
                                  >
                                    <div className="text-xs tabular-nums leading-tight">
                                      <span className={`font-semibold ${statusTextColor(w.status)}`}>
                                        {w.fact != null ? formatCurrency(w.fact, 'RUB') : '—'}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground tabular-nums leading-tight">
                                      план {w.plan != null ? formatCurrency(w.plan, 'RUB') : '—'}
                                    </div>
                                    <div className="mt-1 flex items-center gap-1.5">
                                      <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                                        <div
                                          className={statusStripe(w.status)}
                                          style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%`, height: '100%' }}
                                        />
                                      </div>
                                      <span className={`text-[10px] font-medium tabular-nums ${statusTextColor(w.status)}`}>
                                        {pct == null ? '—' : formatPct(pct)}
                                      </span>
                                    </div>
                                    <div className="mt-1 text-[10px] text-muted-foreground leading-tight">
                                      {fmtDayMonth(w.start)} → {fmtDayMonth(w.end)}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>

                        {isAdvanced && isFormOpen && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Calendar size={14} className="text-muted-foreground" />
                            <label className="text-xs text-muted-foreground" htmlFor={`unassign-${p.assignment_id}`}>
                              Снять с даты
                            </label>
                            <input
                              id={`unassign-${p.assignment_id}`}
                              type="date"
                              value={unassignDate}
                              onChange={e => setUnassignDate(e.target.value)}
                              className="px-2 py-1 border border-border rounded text-sm bg-background"
                            />
                            {/* Показываем, что именно запишется: сама дата — первый день БЕЗ
                                франчайзи, в назначение ляжет день до неё. */}
                            <span className="text-xs text-muted-foreground">
                              {unassignDate <= p.assigned_from
                                ? 'назначение будет удалено полностью'
                                : `числится по ${fmtDate(lastDayBefore(unassignDate))} включительно`}
                            </span>
                            <ActionButton
                              tone="primary"
                              disabled={busyId === p.assignment_id || !unassignDate}
                              onClick={() => handleUnassignSubmit(p)}
                            >
                              {unassignDate > todayISO()
                                ? 'Запланировать'
                                : unassignDate === todayISO()
                                  ? 'Снять сегодня'
                                  : 'Снять задним числом'}
                            </ActionButton>
                            <ActionButton onClick={() => setUnassignFormId(null)}>
                              Отмена
                            </ActionButton>
                          </div>
                        )}

                        {/* Studio list — single line each, with header */}
                        <div className="space-y-1">
                          {p.studioStatuses.length === 0 ? (
                            <>
                              <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-1">Студии</div>
                              <div className="text-xs text-muted-foreground px-1 py-1">
                                Активных студий для текущего периода не найдено.
                              </div>
                            </>
                          ) : (
                            <div className="bg-background rounded-md overflow-hidden divide-y divide-border/60">
                              {/* Studio table header */}
                              <div className="hidden sm:flex items-center px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/30">
                                <div className="w-0.5 shrink-0" />
                                <div className="flex-1 min-w-0 pl-2">Студия</div>
                                <div className="shrink-0 grid grid-cols-4 sm:grid-cols-12 gap-x-3 sm:gap-x-4 gap-y-0">
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Выручка</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Маркетинг</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Лиды</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Звонки</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Дозвоны</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Продажи</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">CR1</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">CR2</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">CR3</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Чек нов</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Чек пост</span>
                                  <span className="min-w-[40px] sm:min-w-[72px] text-center">Возвраты</span>
                                </div>
                                <div className="w-5 ml-1" />
                              </div>
                              {/* Mobile-only caption */}
                              <div className="sm:hidden px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/30">
                                Студии
                              </div>
                              {p.studioStatuses.map(({ studio, status }) => (
                                <StudioRow
                                  key={`${p.assignment_id}-${studio.studio_internal_id}`}
                                  studio={studio}
                                  status={status}
                                  onOpen={() => {
                                    if (studio.studio_internal_id) {
                                      navigate(`/partners/${p.partner_number}/studios/${studio.studio_internal_id}/metrics`)
                                    }
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Снятые франчайзи. Витрина активных назначений их не показывает, поэтому
              без этого блока ошибочную дату снятия исправить неоткуда. */}
          {isAdvanced && closedAssignments.length > 0 && (
            <Card>
              <button
                type="button"
                className="w-full flex items-center gap-2 text-left"
                onClick={() => setClosedOpen(v => !v)}
              >
                {closedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="text-sm font-medium">Снятые франчайзи</span>
                <span className="text-sm text-muted-foreground tabular-nums">{closedAssignments.length}</span>
              </button>

              {closedOpen && (
                <div className="mt-3 space-y-1">
                  {closedAssignments.map(a => {
                    const isEditing = closedFormId === a.assignment_id
                    return (
                      <div key={a.assignment_id} className="rounded-md border border-border/70 px-2 py-1.5">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-sm font-medium">
                            {formatPartnerDisplay(a.partner_number, a.persons_fio)}
                          </span>
                          {a.cities_text && (
                            <span className="text-[11px] text-muted-foreground truncate">{a.cities_text}</span>
                          )}
                          <span className="text-[11px] text-muted-foreground tabular-nums">
                            {fmtDate(a.assigned_from)} → снят с {fmtDate(a.unassigned_at)}
                          </span>
                          {a.auto_closed_by_role_end && (
                            <Badge variant="secondary">снят автоматически (конец роли ТУ)</Badge>
                          )}
                          <div className="ml-auto flex items-center gap-2">
                            {!isEditing && (
                              <>
                                <ActionButton
                                  disabled={busyId === a.assignment_id}
                                  onClick={() => {
                                    setClosedDate(a.unassigned_at)
                                    setClosedFormId(a.assignment_id)
                                  }}
                                >
                                  Изменить дату
                                </ActionButton>
                                <ActionButton
                                  disabled={busyId === a.assignment_id}
                                  onClick={() => handleReopenClosed(a)}
                                  title="Вернуть франчайзи этому ТУ"
                                >
                                  Вернуть
                                </ActionButton>
                              </>
                            )}
                          </div>
                        </div>

                        {isEditing && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Calendar size={14} className="text-muted-foreground" />
                            <label className="text-xs text-muted-foreground" htmlFor={`closed-${a.assignment_id}`}>
                              Снять с даты
                            </label>
                            <input
                              id={`closed-${a.assignment_id}`}
                              type="date"
                              value={closedDate}
                              onChange={e => setClosedDate(e.target.value)}
                              className="px-2 py-1 border border-border rounded text-sm bg-background"
                            />
                            <span className="text-xs text-muted-foreground">
                              {closedDate <= a.assigned_from
                                ? 'назначение будет удалено полностью'
                                : `числится по ${fmtDate(lastDayBefore(closedDate))} включительно`}
                            </span>
                            <ActionButton
                              tone="primary"
                              disabled={busyId === a.assignment_id || !closedDate}
                              onClick={() => handleClosedDateSubmit(a)}
                            >
                              Сохранить дату
                            </ActionButton>
                            <ActionButton onClick={() => setClosedFormId(null)}>
                              Отмена
                            </ActionButton>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {!loading && !error && !managerInfo && (
        <Card><div className="text-sm text-muted-foreground">Управляющий не найден.</div></Card>
      )}
    </div>
  )
}

interface FilterChipProps {
  active: boolean
  onClick: () => void
  label: string
  color?: 'error' | 'success' | 'muted'
}

function FilterChip({ active, onClick, label, color }: FilterChipProps) {
  const activeClass = (() => {
    if (!active) return 'bg-muted/40 text-muted-foreground hover:bg-muted'
    if (color === 'error') return 'bg-error/15 text-error'
    if (color === 'success') return 'bg-success/15 text-success'
    if (color === 'muted') return 'bg-muted text-foreground'
    return 'bg-primary/15 text-primary'
  })()
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${activeClass}`}
    >
      {label}
    </button>
  )
}

interface SummaryMetricProps {
  label: string
  plan: number | null
  fact: number | null
  expected: number
  formatter?: (value: number) => string
  planFormatter?: (value: number) => string
  factFormatter?: (value: number) => string
}

function SummaryMetric({
  label,
  plan,
  fact,
  expected,
  formatter = formatPlainValue,
  planFormatter,
  factFormatter,
}: SummaryMetricProps) {
  const status = metricStatus(plan, fact, expected)
  const safePlan = plan ?? 0
  const safeFact = fact ?? 0
  const factPct = safePlan > 0 ? (safeFact / safePlan) * 100 : null
  const formatPlan = planFormatter ?? formatter
  const formatFact = factFormatter ?? formatter
  return (
    <div className="rounded-md border border-border/70 bg-background p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">
        <span className="font-semibold">{fact != null ? formatFact(fact) : '—'}</span>
        <span className="text-muted-foreground"> / {plan != null ? formatPlan(plan) : '—'}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
          <div
            className={statusStripe(status)}
            style={{ width: `${Math.min(100, Math.max(0, factPct ?? 0))}%`, height: '100%' }}
          />
        </div>
        <span className={`text-[11px] font-medium ${statusTextColor(status)}`}>
          {factPct == null ? '—' : formatPct(factPct)}
        </span>
      </div>
    </div>
  )
}

interface MoneySummaryMetricRow {
  currency: string
  plan: number | null
  fact: number | null
}

interface MoneySummaryMetricProps {
  label: string
  rows: MoneySummaryMetricRow[]
  expected: number
  isAverage?: boolean
}

function MoneySummaryMetric({ label, rows, expected, isAverage = false }: MoneySummaryMetricProps) {
  const visibleRows = rows.filter((row) => row.plan != null || row.fact != null)

  return (
    <div className="rounded-md border border-border/70 bg-background p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 space-y-1.5">
        {visibleRows.length === 0 ? (
          <div className="text-sm text-muted-foreground">—</div>
        ) : (
          visibleRows.map((row) => {
            const status = metricStatus(row.plan, row.fact, expected)
            const safePlan = row.plan ?? 0
            const safeFact = row.fact ?? 0
            const factPct = safePlan > 0 ? (safeFact / safePlan) * 100 : null

            return (
              <div key={`${label}-${row.currency}`}>
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="font-medium text-muted-foreground">{row.currency}</span>
                  <span className="text-muted-foreground">
                    {row.fact != null ? formatCurrency(row.fact, row.currency) : '—'}
                    {' / '}
                    {row.plan != null ? formatCurrency(row.plan, row.currency) : '—'}
                  </span>
                </div>
                {!isAverage && (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                      <div
                        className={statusStripe(status)}
                        style={{ width: `${Math.min(100, Math.max(0, factPct ?? 0))}%`, height: '100%' }}
                      />
                    </div>
                    <span className={`text-[11px] font-medium ${statusTextColor(status)}`}>
                      {factPct == null ? '—' : formatPct(factPct)}
                    </span>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

interface MiniMetricProps {
  fact: number | null | undefined
  plan: number | null | undefined
  status: Status
  /** If true, value is shown as absolute percentage (e.g. CR rate). Else as raw number. */
  isRate?: boolean
}

function MiniMetric({ fact, plan, status, isRate }: MiniMetricProps) {
  const fmt = (v: number | null | undefined) => {
    if (v == null) return '—'
    if (isRate) return formatPercentage(v)
    return formatCompact(v)
  }
  return (
    <div className="min-w-[40px] sm:min-w-[72px] text-center leading-tight tabular-nums">
      <div className={`text-[11px] sm:text-sm font-semibold ${statusTextColor(status)}`}>
        {fmt(fact)}
      </div>
      <div className="text-[10px] sm:text-xs text-muted-foreground">
        {fmt(plan)}
      </div>
    </div>
  )
}

interface StudioRowProps {
  studio: TerritorialManagerPartnerPeriodDetailRow
  status: Status
  onOpen: () => void
}

function StudioRow({ studio, status, onOpen }: StudioRowProps) {
  const marketingStatus = metricStatus(studio.studio_plan_marketing_budget, studio.studio_fact_marketing_spent, 1)
  const salesStatus = rateStatus(studio.studio_plan_sales_count, studio.studio_fact_sales_count)
  const leadsStatus = rateStatus(studio.studio_plan_leads_count, studio.studio_fact_leads_count)
  const callsStatus = metricStatus(studio.studio_plan_calls_count, studio.studio_fact_calls_count, 1)
  const answeredCallsStatus = metricStatus(studio.studio_plan_answered_calls_count, studio.studio_fact_answered_calls_count, 1)
  const cr1Status = rateStatus(studio.studio_plan_booking_conversion, studio.studio_fact_booking_conversion)
  const cr2Status = rateStatus(studio.studio_plan_visit_conversion, studio.studio_fact_visit_conversion)
  const cr3Status = rateStatus(studio.studio_plan_sales_conversion, studio.studio_fact_sales_conversion)
  const avgNewStatus = rateStatus(studio.studio_plan_avg_check_new, studio.studio_fact_avg_check_new)
  const avgExistingStatus = rateStatus(studio.studio_plan_avg_check_existing, studio.studio_fact_avg_check_existing)
  const refundsStatus = metricStatus(studio.studio_plan_refunds_amount, studio.studio_fact_refunds_amount, 1)

  const title = studio.studio_number
    ? [studio.studio_number, studio.studio_city_name, studio.studio_address]
        .filter((s): s is string => !!s && s.trim().length > 0)
        .join(' · ')
    : 'Студия не найдена'

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left flex items-center hover:bg-muted/30 transition"
    >
      <div className={`w-0.5 self-stretch shrink-0 ${statusStripe(status)}`} />
      <div className="flex-1 min-w-0 flex items-center gap-1.5 pl-2 pr-1 py-1.5">
        <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${statusDot(status)}`} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">{title}</div>
          {status === 'no-plan' && (
            <div className="text-[10px] text-muted-foreground truncate">нет плана</div>
          )}
        </div>
      </div>
      <div className="shrink-0 grid grid-cols-4 sm:grid-cols-12 gap-x-3 sm:gap-x-4 gap-y-0">
        <MiniMetric fact={studio.studio_fact_gross_revenue} plan={studio.studio_plan_gross_revenue} status={status} />
        <MiniMetric fact={studio.studio_fact_marketing_spent} plan={studio.studio_plan_marketing_budget} status={marketingStatus} />
        <MiniMetric fact={studio.studio_fact_leads_count} plan={studio.studio_plan_leads_count} status={leadsStatus} />
        <MiniMetric fact={studio.studio_fact_calls_count} plan={studio.studio_plan_calls_count} status={callsStatus} />
        <MiniMetric fact={studio.studio_fact_answered_calls_count} plan={studio.studio_plan_answered_calls_count} status={answeredCallsStatus} />
        <MiniMetric fact={studio.studio_fact_sales_count} plan={studio.studio_plan_sales_count} status={salesStatus} />
        <MiniMetric fact={studio.studio_fact_booking_conversion} plan={studio.studio_plan_booking_conversion} status={cr1Status} isRate />
        <MiniMetric fact={studio.studio_fact_visit_conversion} plan={studio.studio_plan_visit_conversion} status={cr2Status} isRate />
        <MiniMetric fact={studio.studio_fact_sales_conversion} plan={studio.studio_plan_sales_conversion} status={cr3Status} isRate />
        <MiniMetric fact={studio.studio_fact_avg_check_new} plan={studio.studio_plan_avg_check_new} status={avgNewStatus} />
        <MiniMetric fact={studio.studio_fact_avg_check_existing} plan={studio.studio_plan_avg_check_existing} status={avgExistingStatus} />
        <MiniMetric fact={studio.studio_fact_refunds_amount} plan={studio.studio_plan_refunds_amount} status={refundsStatus} />
      </div>
      <div className="w-5 shrink-0 text-muted-foreground ml-1">
        <ChevronRight size={12} />
      </div>
    </button>
  )
}
